import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "./Reveal";
import ContactHeroVisual from "@/components/contact/ContactHeroVisual";
import { BTN_PRIMARY, BTN_GHOST_DARK, HOME_WRAP } from "./homeTheme";

/**
 * Final conversion band — the section immediately above the footer. Copy and both buttons come from `data.cta`
 * (Admin → Home Page → CTA); the visual on the right is the same real Shingare product-cutout composition used on the
 * Contact page hero (`ContactHeroVisual` — tube cleaner, pipe beveling, tube expansion, torque wrench), not the CMS
 * `cta.image` photo. That photo was a generic/third-party equipment shot (branded "S POWER", not Shingare) and is no
 * longer rendered here — see [[shingare-footer-cta]]. Kept more compact than the Contact hero (shorter section, no
 * breadcrumb/eyebrow-per-product captions) by simply giving the visual less column width; nothing about the component
 * itself changes, so both places stay visually consistent.
 */
export default function QuoteCTA({ data }) {
  const cta = data?.cta || {};
  if (!cta.heading) return null;
  return (
    <section className="relative isolate overflow-hidden bg-[#071B3A] py-16 text-white lg:py-20" aria-labelledby="home-cta-title">
      <div className="pointer-events-none absolute -left-32 top-10 -z-10 h-[380px] w-[380px] rounded-full bg-[#1769FF]/20 blur-[130px]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.06] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:56px_56px] [mask-image:radial-gradient(ellipse_60%_80%_at_20%_40%,#000,transparent)]" aria-hidden="true" />

      <div className={`${HOME_WRAP} grid items-center gap-8 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] md:gap-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10`}>
        <Reveal className="max-w-[620px]">
          <div className="mb-5 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#7FB0FF]">
            <span className="h-px w-8 bg-[#7FB0FF]" aria-hidden="true" />
            Get in Touch
          </div>
          <h2 id="home-cta-title" className="font-heading text-[30px] font-semibold leading-[1.12] tracking-[-0.02em] sm:text-[38px] lg:text-[44px]">{cta.heading}</h2>
          {cta.description && <p className="mt-5 text-base leading-relaxed text-white/75 lg:text-lg">{cta.description}</p>}
          <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:gap-4">
            {cta.primaryButton?.label && (
              <Link to={cta.primaryButton.link || "/contact"} className={BTN_PRIMARY} data-testid="cta-quote">
                {cta.primaryButton.label}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
              </Link>
            )}
            {cta.secondaryButton?.label && (
              <Link to={cta.secondaryButton.link || "/contact"} className={BTN_GHOST_DARK} data-testid="cta-engineer">
                {cta.secondaryButton.label}
              </Link>
            )}
          </div>
        </Reveal>

        <ContactHeroVisual />
      </div>
    </section>
  );
}
